/**
 * UI copy for Goodbye Letter prereq errors. Keeps the data-builder service
 * typed errors + payload only. Each `GoodbyeLetterPrereqCode` maps to a
 * `{ title, detail }` pair surfaced as a toast by ServicingLoanDetail's
 * one-click download handler.
 */

import {
  GoodbyeLetterPrereqError,
  type GoodbyeLetterPrereqCode
} from '@/types/goodbye-letter';
import type { GoodbyeSendResult } from '@/utils/goodbye-letter/sendResult';

export interface ResolvedGoodbyeLetterErrorMessage {
  code: GoodbyeLetterPrereqCode;
  title: string;
  detail: string;
}

const FAMILY_LABELS: Partial<Record<GoodbyeLetterPrereqCode, string>> = {
  LOAN_DATA_INCOMPLETE: 'Loan data is incomplete',
  TEMPLATE_DATA_INCOMPLETE: 'Investor template data is incomplete',
  SETTINGS_INCOMPLETE: 'Goodbye Letter Settings are incomplete',
  TRANSFEROR_DATA_INCOMPLETE: 'Lendwise company profile is incomplete'
};

export function resolveGoodbyeLetterErrorMessage(
  err: GoodbyeLetterPrereqError
): ResolvedGoodbyeLetterErrorMessage {
  switch (err.code) {
    case 'PAYMENT_DATA_INCOMPLETE':
    case 'PAYMENT_DATA_CONFLICT':
    case 'PAYMENT_PERIOD_UNSUPPORTED':
      return { code: err.code, title: 'Payment needs review', detail: err.message };
    case 'SERVICING_LOAN_NOT_FOUND':
      return { code: err.code, title: 'Servicing loan not found', detail: err.message };
    case 'ENCOMPASS_LOAN_NOT_FOUND':
      return { code: err.code, title: 'Encompass loan not found', detail: err.message };
    case 'BANKER_RECON_MISSING':
      return {
        code: err.code,
        title: 'Banker recon required',
        detail:
          'This servicing loan needs a banker recon row before the Goodbye Letter can be generated. ' +
          'Open the banker-recon workspace to create or link one.'
      };
    case 'SERVICER_NOT_SELECTED':
      return { code: err.code, title: 'Servicer not selected', detail: "Open this loan's Banker Recon, choose the servicer on the Servicing tab, and finalize again." };
    case 'NO_TEMPLATE':
      return {
        code: err.code,
        title: 'No investor template',
        detail:
          'This investor has no Encompass investor template linked. Link a template from ' +
          'Manage Investors → Investor Templates.'
      };
    case 'TEMPLATE_AMBIGUOUS':
      return {
        code: err.code,
        title: 'Multiple investor templates linked',
        detail:
          'This investor has multiple Encompass templates and no deterministic product scope. ' +
          'Open Manage Investors → Investor Templates and assign products to each template.'
      };
    case 'TEMPLATE_PRODUCT_UNRESOLVED':
      return {
        code: err.code,
        title: 'Loan product not resolved',
        detail: 'The loan product could not be resolved for template selection. Review the loan product fields and try again.'
      };
    case 'TEMPLATE_PRODUCT_NOT_ASSIGNED':
    case 'TEMPLATE_SCOPE_MISSING':
      return {
        code: err.code,
        title: 'No template for product',
        detail: 'No investor template is assigned to this product. Open Manage Investors → Investor Templates and assign product scope.'
      };
    case 'TEMPLATE_PRODUCT_AMBIGUOUS':
      return {
        code: err.code,
        title: 'Multiple templates for product',
        detail: 'More than one investor template applies to this product. Resolve overlapping product assignments in Manage Investors.'
      };
    case 'TEMPLATE_PRODUCT_MISMATCH':
      return {
        code: err.code,
        title: 'Stored template mismatch',
        detail: 'The servicing loan references a template that no longer matches the investor/product scope. Review the investor template assignment.'
      };
    case 'TEMPLATE_NOT_SYNCED':
      return {
        code: err.code,
        title: 'Template not synced',
        detail:
          'The investor template has not synced from Encompass. Click Sync on the template, then try again.'
      };
    case 'GOODBYE_SETTINGS_MISSING':
      return {
        code: err.code,
        title: 'Goodbye Letter Settings not configured',
        detail:
          "No Goodbye Letter Settings have been configured for this investor's template. " +
          "Open the template's Goodbye Letter Settings card under Manage Investors and fill them in."
      };
    case 'LOAN_DATA_INCOMPLETE':
    case 'TEMPLATE_DATA_INCOMPLETE':
    case 'SETTINGS_INCOMPLETE':
    case 'TRANSFEROR_DATA_INCOMPLETE': {
      const missingList = err.missingFields?.length ? ` Missing: ${err.missingFields.join(', ')}.` : '';
      return {
        code: err.code,
        title: FAMILY_LABELS[err.code] ?? err.code,
        detail: `${err.message}${missingList} Update the underlying data and try again.`
      };
    }
    default:
      return {
        code: err.code,
        title: 'Unable to generate Goodbye Letter',
        detail: err.message
      };
  }
}

/**
 * Outcome of a send-goodbye-letter call in plain words. Raw sender values
 * (codes, reasons, error strings) are never shown; callers log them.
 * 'unconfirmed' means the email may or may not have gone out.
 */
export type GoodbyeOutcomeState = 'not_sent' | 'unconfirmed';

export interface GoodbyeOutcome {
  state: GoodbyeOutcomeState;
  text: string;
}

const SEND_FROM_SERVICING = 'Once fixed, send it from Servicing → Send goodbye letter.';
const SERVICE_ERROR_TEXT = 'The letter service hit an error. Try again from Servicing → Send goodbye letter.';

// The sender's prerequisite response carries the code but not the message.
const PAYMENT_DEFAULT_DETAILS: Partial<Record<GoodbyeLetterPrereqCode, string>> = {
  PAYMENT_DATA_CONFLICT: "The payment in Encompass doesn't match the Closing Disclosure. Review the payment fields, synchronize the loan, and retry.",
  PAYMENT_DATA_INCOMPLETE: 'Payment information is missing in Encompass. Complete it, synchronize the loan, and retry.',
  PAYMENT_PERIOD_UNSUPPORTED: "This loan's payment schedule isn't supported for automatic letters."
};

const MACHINE_VALUE_TEXT: Record<string, string> = {
  no_borrower_email: 'The borrower has no email address in Encompass. Add it, then send it from Servicing → Send goodbye letter.',
  disabled: 'Goodbye letter emails are turned off in Company Settings → Email Notifications.',
  banker_recon_missing: "The servicing record isn't linked to a finalized recon.",
  banker_recon_not_finalized: "The servicing record isn't linked to a finalized recon.",
  servicing_loan_not_found: "This loan's servicing record wasn't found.",
  caller_org_mismatch: "This loan's servicing record wasn't found.",
  already_sent: 'A goodbye letter was already sent for this loan.',
  historical_loan: "Historical loans don't get goodbye letters."
};

const humanizeFieldNames = (fields?: string[]): string[] | undefined => fields?.map(field => field.replace(/_/g, ' '));

export function describeGoodbyeSendResult(result: GoodbyeSendResult): GoodbyeOutcome {
  if (result.transportFailure) {
    return {
      state: 'unconfirmed',
      text: "The letter service didn't respond, so delivery isn't confirmed. Check the loan in Servicing before resending."
    };
  }
  const value = result.error ?? result.reason;
  if (value === 'goodbye_delivery_unconfirmed') {
    return {
      state: 'unconfirmed',
      text: 'An earlier send was not confirmed by the email provider. Check the loan in Servicing before resending.'
    };
  }
  if (result.code) {
    const code = result.code as GoodbyeLetterPrereqCode;
    const paymentDetail = PAYMENT_DEFAULT_DETAILS[code];
    if (paymentDetail) {
      return { state: 'not_sent', text: paymentDetail };
    }
    const resolved = resolveGoodbyeLetterErrorMessage(
      new GoodbyeLetterPrereqError(code, '', humanizeFieldNames(result.missingFields))
    );
    const detail = resolved.detail.trim();
    return { state: 'not_sent', text: `${resolved.title}.${detail ? ` ${detail}` : ''} ${SEND_FROM_SERVICING}` };
  }
  if (value && MACHINE_VALUE_TEXT[value]) {
    return { state: 'not_sent', text: MACHINE_VALUE_TEXT[value] };
  }
  // Sender-side failures: *_failed, *_unavailable, failed_to_load_*, unexpected_send_failure.
  if (value && /^([a-z_]+_(failed|unavailable)|failed_to_[a-z_]+|unexpected_send_failure)$/.test(value)) {
    return { state: 'not_sent', text: SERVICE_ERROR_TEXT };
  }
  return { state: 'not_sent', text: 'The borrower notice did not send.' };
}

/** False for a delivered letter and for skips that need no action (already sent, historical loan). */
export function shouldReportGoodbyeOutcome(result: GoodbyeSendResult): boolean {
  if (result.transportFailure) {
    return true;
  }
  if (result.success === true && !result.skipped) {
    return false;
  }
  const value = result.reason ?? result.error;
  return !(result.skipped && (value === 'already_sent' || value === 'historical_loan'));
}
